import {
  BodyComponentConsts,
  ItemTableConsts,
  SudoConsts,
} from 'client/util/Constants';
import {
  getOverallTooltipText,
  getPushTooltipText,
  getTotpTooltipText,
  getWebAuthnTooltipText,
} from './systemUserMfaTooltipTextHelpers';

const { columnTypes } = ItemTableConsts;
/**
 * Format the user name to display as `lastname, firstname`
 */
const formatUserNameDisplay = (user) => {
  let name;
  if (user.lastname && user.firstname) {
    name = `${user.lastname}, ${user.firstname}`;
  } else {
    name = user.lastname || user.firstname || '';
  }
  return name;
};

const passwordNeverExpires = (user) => (user.passwordNeverExpires
  || !user.password_expiration_date);

/**
 * This object lists the column options available for each resource.
 * This is used to populate the columns dropdown for the item table.
 *
 * @todo This will eventually *partially* be dynamically generated by an endpoint (FE-409)
 *
 * @todo We will be able to remove `activated` and `suspended` once these fields are removed
 * from the mongo database in follow-on work from DE.
 * @see https://jumpcloud.atlassian.net/browse/DE-1574
 *
 * @param {Object} bodyComponent - The component to be rendered for that column
 * @param {String} dataField - The path of the field to retrieve the value
 * @param {String} name - Then name of the column
 * @param {String} sortBy - The field on which the data should be sorted for this column
 */
const metadata = {
  accountLocked: {
    dataField: 'account_locked',
    name: 'Account Locked',
    sortBy: 'account_locked',
  },
  activated: {
    dataField: 'activated',
    name: 'Activated',
    sortBy: 'activated',
  },
  allowPublicKey: {
    dataField: 'allow_public_key',
    name: 'Allow Public Key',
    sortBy: 'allow_public_key',
  },
  bindStatus: {
    bodyComponent: 'BindStatus',
    name: 'Bind Status',
  },
  company: {
    dataField: 'company',
    name: 'Company',
    sortBy: 'company',
  },
  costCenter: {
    dataField: 'costCenter',
    name: 'Cost Center',
    sortBy: 'costCenter',
  },
  department: {
    dataField: 'department',
    name: 'Department',
    sortBy: 'department',
  },
  description: {
    dataField: 'description',
    name: 'Description',
    sortBy: 'description',
  },
  disableDeviceMaxLoginAttempts: {
    dataField: 'disableDeviceMaxLoginAttempts',
    name: 'Disable Device Max Login Attempts',
    sortBy: 'disableDeviceMaxLoginAttempts',
  },
  email: {
    bodyComponent: 'AdBridgeLabel',
    classNames: {
      body: ['emailColumn'],
      heading: ['emailColumn'],
    },
    dataField: 'email',
    name: 'Email',
    sortBy: 'email',
  },
  employeeType: {
    dataField: 'employeeType',
    name: 'Employee Type',
    sortBy: 'employeeType',
  },
  externalDn: {
    dataField: 'external_dn',
    name: 'External DN',
    sortBy: 'external_dn',
  },
  externalSourceType: {
    dataField: 'external_source_type',
    name: 'External Source Type',
    sortBy: 'external_source_type',
  },
  externallyManaged: {
    dataField: 'externally_managed',
    name: 'Externally Managed',
    sortBy: 'externally_managed',
  },
  globalAdminSudo: {
    dataField: 'sudo',
    name: SudoConsts.globalSudo,
    headerName: 'Global Administrator/Sudo',
    sortBy: 'sudo',
  },
  globalPasswordlessSudo: {
    dataField: 'passwordless_sudo',
    name: SudoConsts.globalPasswordless,
    headerName: 'Global Passwordless Sudo',
    sortBy: 'passwordless_sudo',
  },
  jobTitle: {
    dataField: 'jobTitle',
    name: 'Job Title',
    sortBy: 'jobTitle',
  },
  ldapBindingUser: {
    dataField: 'ldap_binding_user',
    name: 'LDAP Binding User',
    sortBy: 'ldap_binding_user',
  },
  location: {
    dataField: 'location',
    name: 'Location',
    sortBy: 'location',
  },
  mfaOverallStatus: {
    bodyComponent: 'SystemUserMFABadgeWithTooltip',
    bodyComponentProps: {
      statusPath: `mfaEnrollment.${BodyComponentConsts.mfaTypes.overall}`,
      getTooltipText: getOverallTooltipText,
    },
    name: 'MFA: Enrollment',
    sortBy: `mfaEnrollment.${BodyComponentConsts.mfaTypes.overall}`,
  },
  mfaPushStatus: {
    bodyComponent: 'SystemUserMFABadgeWithTooltip',
    bodyComponentProps: {
      statusPath: `mfaEnrollment.${BodyComponentConsts.mfaTypes.push}`,
      getTooltipText: getPushTooltipText,
    },
    name: 'MFA: JumpCloud Protect',
    sortBy: `mfaEnrollment.${BodyComponentConsts.mfaTypes.push}`,
  },
  mfaTotpStatus: {
    bodyComponent: 'SystemUserMFABadgeWithTooltip',
    bodyComponentProps: {
      statusPath: `mfaEnrollment.${BodyComponentConsts.mfaTypes.totp}`,
      getTooltipText: getTotpTooltipText,
    },
    name: 'MFA: TOTP',
    sortBy: `mfaEnrollment.${BodyComponentConsts.mfaTypes.totp}`,
  },
  mfaUserRequirementStatus: {
    bodyComponent: 'SystemUserMFARequirementBadgeWithTooltip',
    name: 'MFA: User Requirement',
    sortBy: 'enable_user_portal_multifactor',
  },
  mfaWebAuthnStatus: {
    bodyComponent: 'SystemUserMFABadgeWithTooltip',
    bodyComponentProps: {
      statusPath: `mfaEnrollment.${BodyComponentConsts.mfaTypes.webAuthn}`,
      getTooltipText: getWebAuthnTooltipText,
    },
    name: 'MFA: WebAuthn',
    sortBy: `mfaEnrollment.${BodyComponentConsts.mfaTypes.webAuthn}`,
  },
  name: {
    bodyComponent: 'ItemName',
    bodyComponentProps: {
      subtitleField: 'username',
      titleFunction: item => formatUserNameDisplay(item),
    },
    name: 'Name',
    sortBy: 'lastname',
  },
  passwordExpiry: {
    bodyComponent: 'FormattedDate',
    bodyComponentProps: {
      type: BodyComponentConsts.formattedDateTypes.userPasswordExpiry,
    },
    classNames: {
      body: [columnTypes.dateColumn],
      heading: [columnTypes.dateColumn],
    },
    name: 'Password Expiration Date',
    sortBy: 'password_expiration_date',
  },
  passwordNeverExpires: {
    bodyComponent: 'PasswordNeverExpiresText',
    bodyComponentProps: {
      getPasswordExpiresText: item => passwordNeverExpires(item),
    },
    name: 'Password Never Expires',
    sortBy: 'password_never_expires',
  },
  passwordStatus: {
    bodyComponent: 'StatusIcon',
    bodyComponentProps: {
      type: BodyComponentConsts.statusIconTypes.systemUser,
    },
    classNames: {
      body: [columnTypes.statusColumn],
      heading: [columnTypes.statusColumn],
    },
    name: 'Password Status',
    sortBy: 'status',
  },
  sambaServiceUser: {
    dataField: 'samba_service_user',
    name: 'Samba Service User',
    sortBy: 'samba_service_user',
  },
  state: {
    bodyComponent: 'UserState',
    classNames: {
      body: [columnTypes.stateColumn],
      heading: [columnTypes.stateColumn],
    },
    name: 'User State',
    sortBy: 'state',
  },
  sudoStatus: {
    bodyComponent: 'SystemUserSudoStatus',
    bodyComponentProps: {
      showNewWorkflow: false,
      // NOTE: This is a placeholder. Viewport must be defined properly when this column is used!
      viewport: {},
    },
    classNames: {
      body: [columnTypes.sudoStatusColumn],
      heading: [columnTypes.sudoStatusColumn],
    },
    name: 'Permissions',
  },
  suggestion: {
    dataField: 'suggestion',
    name: 'Suggestion',
    sortBy: 'suggestion',
  },
  suspended: {
    dataField: 'suspended',
    name: 'Suspended',
    sortBy: 'suspended',
  },
  systemUsername: {
    dataField: 'systemUsername',
    name: 'System Username',
    sortBy: 'systemUsername',
  },
  unixGuid: {
    dataField: 'unix_guid',
    name: 'Unix GUID',
    sortBy: 'unix_guid',
  },
  unixUid: {
    dataField: 'unix_uid',
    name: 'Unix UID',
    sortBy: 'unix_uid',
  },
};

Object.keys(metadata).forEach((columnName) => {
  const column = metadata[columnName];
  column.headingText = column.headerName || column.name;
});

export const mainPanelColumnOptions = [
  metadata.accountLocked,
  metadata.activated,
  metadata.allowPublicKey,
  metadata.company,
  metadata.costCenter,
  metadata.department,
  metadata.description,
  metadata.disableDeviceMaxLoginAttempts,
  metadata.email,
  metadata.employeeType,
  metadata.externalDn,
  metadata.externalSourceType,
  metadata.externallyManaged,
  metadata.globalAdminSudo,
  metadata.globalPasswordlessSudo,
  metadata.jobTitle,
  metadata.ldapBindingUser,
  metadata.location,
  metadata.mfaOverallStatus,
  metadata.mfaPushStatus,
  metadata.mfaTotpStatus,
  metadata.mfaUserRequirementStatus,
  metadata.mfaWebAuthnStatus,
  metadata.name,
  metadata.passwordExpiry,
  metadata.passwordNeverExpires,
  metadata.passwordStatus,
  metadata.state,
  metadata.suspended,
  metadata.systemUsername,
  metadata.unixGuid,
  metadata.unixUid,
];

export default metadata;
